import { useState } from "preact/hooks";

import { ContactFormSheet } from "../components/ContactFormSheet";
import { RecordDetailOverlay } from "../components/RecordDetailOverlay";
import {
  archiveContactConfirmTitle,
  ARCHIVE_CONTACT_BUTTON_LABEL,
  ARCHIVE_CONTACT_CONFIRM_BODY,
  ARCHIVE_CONTACT_CONFIRM_LABEL,
  CONTACT_ARCHIVED_TAG_LABEL,
  RESTORE_CONTACT_BUTTON_LABEL,
  SAVED_MESSAGE,
} from "../lib/recordsCopy";
import type { ContactOut } from "../types";

export interface ContactDetailOverlayProps {
  contact: ContactOut;
  /** Reads (this overlay itself) are admin/manager — a member never reaches
   * the Contacts list at all (`GET /contacts` 403s for member, ADR-002
   * §5/§11 — see `ContactsView`'s gating). Writes (Edit/Archive/Restore)
   * are a stricter gate on top of that: admin/api only, never manager (SA
   * ruling `sa-ruling-crud-endpoints-2026-07-10.md` G-1) — mirrors
   * `ProjectDetailOverlay`'s identical read-vs-write split. */
  isAdmin: boolean;
  onClose: () => void;
  onNotify: (message: string) => void;
  /** `PATCH /contacts/{id}`, threaded from the parent's
   * `useContacts().patchContact` so the fetched list array (this
   * component's `contact` prop is derived from it via `.find()`) stays the
   * single source of truth. */
  onSaveContact: (
    contactId: string,
    body: { name?: string; contact?: string | null; notes?: string | null },
  ) => Promise<void>;
  /** Sprint-013 C3 (design-spec-animal-cards-2026-07-10.md §5) — the
   * Archive confirm's submit, threaded from `useContacts().archiveContact`.
   * The caller (`ContactsView`) is responsible for closing this overlay and
   * showing the snackbar on success, mirroring `ProjectDetailOverlay`'s
   * `onArchive` contract exactly. */
  onArchive?: () => Promise<void>;
  /** Sprint-013 C3 (SA ruling Decision 3.4) — the Restore action's submit,
   * threaded from `useContacts().restoreContact`. Unlike `onArchive`, this
   * does NOT go through a `ConfirmDialog` (restoring is reversible/
   * non-destructive — see `lib/recordsCopy.ts`'s own note on this). */
  onRestore?: () => Promise<void>;
}

/**
 * Sprint-013 C3 (design-spec-animal-cards-2026-07-10.md §5, sa-ruling-
 * crud-endpoints-2026-07-10.md Decision 3, mirroring the C1/C2 Animals/
 * Projects CRUD precedent) — a contact's detail screen: name, phone/email,
 * notes, and the admin-only Edit (via `ContactFormSheet`), Archive
 * (`ConfirmDialog`), and Restore affordances. The "Archived" tag is
 * read-visible to every role that can already view the contact (same
 * posture as `ProjectDetailOverlay`'s "Archived" tag), independent of the
 * write affordances themselves.
 *
 * No "Edited" chip here (unlike `ProjectDetailOverlay`/`AnimalDetailOverlay`)
 * — `ContactOut` carries no `created_at`/`updated_at` (`app/schemas/
 * contacts.py::ContactOut` never has, this phase's A3 widening didn't add
 * them either); nothing to derive that chip from.
 */
export function ContactDetailOverlay({
  contact,
  isAdmin,
  onClose,
  onNotify,
  onSaveContact,
  onArchive,
  onRestore,
}: ContactDetailOverlayProps) {
  const isArchived = contact.archived_at != null;
  const [editing, setEditing] = useState(false);

  return (
    <RecordDetailOverlay
      onClose={onClose}
      title={contact.name}
      backLabel="Back to Contacts"
      isAdmin={isAdmin}
      isArchived={isArchived}
      // No "Edited" chip — `ContactOut` carries no created_at/updated_at.
      showEditedChip={false}
      archivedTagLabel={CONTACT_ARCHIVED_TAG_LABEL}
      onEdit={isAdmin ? () => setEditing(true) : undefined}
      onArchive={onArchive}
      onRestore={onRestore}
      archiveConfirm={{
        title: archiveContactConfirmTitle(contact.name),
        body: ARCHIVE_CONTACT_CONFIRM_BODY,
        confirmLabel: ARCHIVE_CONTACT_CONFIRM_LABEL,
      }}
      archiveButtonLabel={ARCHIVE_CONTACT_BUTTON_LABEL}
      restoreButtonLabel={RESTORE_CONTACT_BUTTON_LABEL}
      onNotify={onNotify}
    >
      {/* ── Contact body slot ── contractor fields (phone/email) + notes. */}
      {contact.contact ? <p class="admin-row-sub">{contact.contact}</p> : null}
      {contact.notes ? (
        <p class="task-title" style={{ marginTop: "10px" }}>
          {contact.notes}
        </p>
      ) : null}

      {isAdmin ? (
        <ContactFormSheet
          open={editing}
          mode="edit"
          contact={contact}
          onCancel={() => setEditing(false)}
          onSave={async (fields) => {
            await onSaveContact(contact.id, fields);
            setEditing(false);
            onNotify(SAVED_MESSAGE);
          }}
        />
      ) : null}
    </RecordDetailOverlay>
  );
}
